import { countConditions } from "@estelle/domain";

import { ForbiddenError, apiFetch } from "@/adapters/api/base";
import type {
  CallAttempt,
  ChangeRow,
  LineItemRow,
  ListQuery,
  Order,
  OrderEvent,
  OrderSeries,
  OrderWithDetails,
  OrdersRepository,
  Page,
  PackingSlipFlag,
  PackingSlipsResult,
  SalesSummary,
  ShipmentRow,
  SkuSeries,
  WriteResult,
} from "@estelle/domain";

/**
 * Orders, over HTTP.
 *
 * Tags, note and cancellation go to `apps/api`'s write routes, which send
 * them through `writeThrough()` to Shopify (#164). What comes back on
 * success is the order as Shopify returned it; a refusal is a `WriteResult`
 * rejection to show, not an error to throw.
 */

function params(query: ListQuery | undefined): URLSearchParams {
  const out = new URLSearchParams();
  if (!query) return out;
  if (query.limit !== undefined) out.set("limit", String(query.limit));
  if (query.cursor !== undefined) out.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    out.set("search", query.search);
  }
  if (query.orderNumbers && query.orderNumbers.length > 0) {
    out.set("numbers", query.orderNumbers.join(","));
  }
  if (query.filter && countConditions(query.filter) > 0) {
    out.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    out.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return out;
}

async function get<T>(path: string, query?: ListQuery): Promise<T> {
  const search = params(query).toString();
  const response = await apiFetch(
    `/api/orders${path}${search ? `?${search}` : ""}`,
  );
  if (!response.ok) {
    // Status and path only: an order response carries personal data, and
    // this message reaches a screen.
    throw new Error(
      `Orders request failed: ${String(response.status)} on ${path || "/"}`,
    );
  }
  return (await response.json()) as T;
}

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx or
 * 503 status — a rejection to show. Anything else is a transport failure and
 * throws, with the status and path only: an order carries personal data.
 */
async function write(
  path: string,
  method: "POST" | "PATCH",
  body: unknown,
): Promise<WriteResult<Order>> {
  const response = await apiFetch(`/api/orders${path}`, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<Order> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Order ${method} failed: ${String(response.status)} on ${path}`,
  );
}

/** A call-attempt write (#636): ERP-only rows, same refusal shape. */
async function writeCall(
  path: string,
  method: "POST" | "PATCH",
  body: unknown,
): Promise<WriteResult<CallAttempt>> {
  const response = await apiFetch(`/api/orders${path}`, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<CallAttempt> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(`Call ${method} failed: ${String(response.status)}`);
}

export function createApiOrders(): OrdersRepository {
  return {
    packingSlips: async (ids, flagged): Promise<PackingSlipsResult> => {
      const response = await apiFetch("/api/packing-slips", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ids: ids.map(Number), flagged }),
      });
      if (response.ok)
        return { kind: "pdf", pdf: await response.arrayBuffer() };
      const body = (await response.json().catch(() => null)) as {
        error?: string;
        flagged?: PackingSlipFlag[];
      } | null;
      if (response.status === 409 && body?.flagged) {
        return { kind: "flagged", flagged: body.flagged };
      }
      return {
        kind: "refused",
        message:
          response.status === 403
            ? "You do not have permission to print packing slips."
            : (body?.error ??
              `Packing slips failed (${String(response.status)}).`),
      };
    },

    list: (query) => get<Page<Order>>("", query),

    get: async (id): Promise<OrderWithDetails | null> => {
      const response = await apiFetch(`/api/orders/${id}`);
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error(`Orders request failed: ${String(response.status)}`);
      }
      return (await response.json()) as OrderWithDetails;
    },

    history: (id, query) => get<Page<OrderEvent>>(`/${id}/history`, query),
    listShipments: (query) => get<Page<ShipmentRow>>("/shipments", query),
    listChanges: (query) => get<Page<ChangeRow>>("/changes", query),
    listLineItems: (query) => get<Page<LineItemRow>>("/line-items", query),

    series: async (query): Promise<OrderSeries> => {
      // The orders list's own filter and search, plus the chart's settings.
      const search = params({ filter: query.filter, search: query.search });
      search.set("from", query.from);
      search.set("to", query.to);
      search.set("bucket", query.bucket);
      search.set("metric", query.metric);
      if (query.splitBy) search.set("splitBy", query.splitBy);
      if (query.includeCancelled) search.set("includeCancelled", "true");

      const response = await apiFetch(
        `/api/orders/series?${search.toString()}`,
      );
      if (response.status === 403) {
        // The route names the permission it wanted; the screen shows it.
        const body = (await response.json().catch(() => null)) as {
          permission?: unknown;
        } | null;
        throw new ForbiddenError(
          "/api/orders/series",
          typeof body?.permission === "string" ? body.permission : undefined,
        );
      }
      if (!response.ok) {
        // Status only — the query string carries the filter, which can name a
        // customer.
        throw new Error(
          `Orders series request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as OrderSeries;
    },

    skuSeries: async (query): Promise<SkuSeries> => {
      const search = new URLSearchParams({
        from: query.from,
        to: query.to,
        bucket: query.bucket,
      });
      if (query.includeCancelled) search.set("includeCancelled", "true");
      for (const key of query.keys ?? []) search.append("keys", key);
      const path = "/api/orders/sku-series";

      const response = await apiFetch(`${path}?${search.toString()}`);
      if (response.status === 403) {
        // The route names the permission it wanted; the screen shows it.
        const body = (await response.json().catch(() => null)) as {
          permission?: unknown;
        } | null;
        throw new ForbiddenError(
          path,
          typeof body?.permission === "string" ? body.permission : undefined,
        );
      }
      if (!response.ok) {
        throw new Error(
          `SKU series request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as SkuSeries;
    },

    salesSummary: async (query): Promise<SalesSummary> => {
      const search = new URLSearchParams({ from: query.from, to: query.to });
      if (query.includeCancelled) search.set("includeCancelled", "true");
      const path = "/api/orders/sales-summary";

      const response = await apiFetch(`${path}?${search.toString()}`);
      if (response.status === 403) {
        // The route names the permission it wanted; the screen shows it.
        const body = (await response.json().catch(() => null)) as {
          permission?: unknown;
        } | null;
        throw new ForbiddenError(
          path,
          typeof body?.permission === "string" ? body.permission : undefined,
        );
      }
      if (!response.ok) {
        throw new Error(
          `Sales summary request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as SalesSummary;
    },

    addTag: (id, tag, guard) =>
      write(`/${encodeURIComponent(id)}/tags`, "POST", { tag, ...guard }),
    removeTag: (id, tag, guard) =>
      write(`/${encodeURIComponent(id)}/tags/remove`, "POST", {
        tag,
        ...guard,
      }),
    setNote: (id, note, guard) =>
      write(`/${encodeURIComponent(id)}/note`, "PATCH", { note, ...guard }),
    cancel: (id, input, guard) =>
      write(`/${encodeURIComponent(id)}/cancel`, "POST", {
        ...input,
        ...guard,
      }),

    callAttempts: (id) =>
      get<CallAttempt[]>(`/${encodeURIComponent(id)}/call-attempts`),
    recordCallAttempt: (id, input) =>
      writeCall(`/${encodeURIComponent(id)}/call-attempts`, "POST", {
        contactId: input.contactId ?? null,
      }),
    setCallOutcome: (id, attemptId, outcome) =>
      writeCall(
        `/${encodeURIComponent(id)}/call-attempts/${encodeURIComponent(attemptId)}`,
        "PATCH",
        { outcome },
      ),
  };
}
